import { useMemo } from 'react';
import { t } from '@lingui/macro';
import { isEmpty } from 'lodash';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_LIST_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { siteFilterId, timeRangeFilterId, statusFilterId, siteGroupFilterId } from '../../filters';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { useIsPgm } from '../../../stores/EnvironmentStore';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';

const title = t`Sites`;

const dataSource = {
    url: '/query/table',
    params: {
        type: 'site',
        entityFields: ['name', 'tags'],
        metrics: ['status', 'siteTestCounts', 'siteInfrCounts'],
        trends: ['status'],
        renderAsEntity: true,
    },
};

const sorters = [{ property: 'status', direction: 'desc' }];

function SiteListPanel(props) {
    const loader = useQueryLoader(dataSource);
    const [isPgm] = useIsPgm();
    const columns = useMemo(
        () => [
            {
                type: COLUMN_TYPES.DRILLINLINK,
                title: t`Site`,
                sortable: true,
                mapping: 'site.name',
                width: 250,
                link: {
                    target: '/site-detail',
                    mappedFilterParams: {
                        [siteFilterId]: 'site',
                    },
                    pageStateParams: ['SelectedTime'],
                },
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: t`Tags`,
                sortable: false,
                mapping: 'site.tags',
                formatter: (tags) => {
                    if (isEmpty(tags)) return DEFAULT_INVALID_VALUE;
                    return tags.join(', ');
                },
            },
            {
                type: COLUMN_TYPES.DRILLINLINK,
                title: t`Local Manager`,
                sortable: true,
                removable: true,
                hidden: !isPgm,
                sortProperty: 'plm',
                mapping: 'plm',
                link: {
                    target: '/sites',
                },
            },
            {
                title: t`Impacted Tests`,
                width: 150,
                sortable: true,
                align: 'center',
                sortProperty: 'DIVIDE(siteTestCounts.affected, siteTestCounts.total)',
                mapper: (row) => `${row.siteTestCounts?.affected} / ${row.siteTestCounts?.total}`,
            },
            {
                title: t`Problem Infrastructure`,
                width: 200,
                sortable: true,
                align: 'center',
                sortProperty: 'DIVIDE(siteInfrCounts.affected, siteInfrCounts.total)',
                mapper: (row) => `${row.siteInfrCounts?.affected} / ${row.siteInfrCounts?.total}`,
            },
            {
                type: COLUMN_TYPES.STATUSTREND,
                title: t`Status Trend`,
                sortable: true,
                sortProperty: 'status',
                mapping: 'trends.status.data',
            },
        ],
        [isPgm]
    );

    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="site.deleted"
        />
    );
}

SiteListPanel.propTypes = panelPropTypes;

SiteListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'SiteList',
    catalogEntry: {
        category: PANEL_CATEGORIES.SITE,
        priority: PANEL_PRIORITIES.LIST,
        title,
        description: t`List of sites, showing status trend, impacted service test counts, and problem infrastructure counts`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [statusFilterId, siteGroupFilterId],
    },
})(SiteListPanel);
